<template>
    <main class="time-edit-page" v-loading="loading">
        <section class="time-edit-card">
            <header class="time-edit-header">
                <div class="time-edit-title">
                    <el-button class="time-back" :icon="isNew ? 'el-icon-close' : 'el-icon-arrow-left'" @click="goBack"></el-button>
                    <h1>{{isNew ? $t('time.createTitle') : $t('time.editTitle')}}</h1>
                </div>
                <div>
                    <el-button v-if="!isNew" type="danger" plain :disabled="form.billed === 1" @click="confirmDelete">{{$t('common.delete')}}</el-button>
                    <el-button type="primary" :loading="saving" @click="save">{{$t('common.save')}}</el-button>
                </div>
            </header>

            <el-form label-position="top" class="time-edit-form">
                <section class="time-edit-section">
                    <h2>{{$t('common.client')}}</h2>
                    <el-select v-model="form.billClientUUID" filterable clearable style="width: 100%" @change="selectClient"
                               :placeholder="$t('time.selectClient')">
                        <el-option v-for="client in clients" :key="client.innerUUID" :label="client.name" :value="client.innerUUID"></el-option>
                    </el-select>
                </section>

                <section class="time-edit-section time-date-grid">
                    <el-form-item :label="$t('time.start')">
                        <el-date-picker v-model="form.start" type="datetime" style="width: 100%" @change="recalculateDuration"></el-date-picker>
                    </el-form-item>
                    <el-form-item :label="$t('time.end')">
                        <el-date-picker v-model="form.end" type="datetime" style="width: 100%" @change="recalculateDuration"></el-date-picker>
                    </el-form-item>
                    <el-form-item :label="$t('time.duration')">
                        <div class="duration-inputs">
                            <el-input-number v-model="durationHours" :min="0" :max="9999" @change="recalculateEnd"></el-input-number><span>{{$t('time.hours')}}</span>
                            <el-input-number v-model="durationMinutes" :min="0" :max="59" @change="recalculateEnd"></el-input-number><span>{{$t('time.minutes')}}</span>
                        </div>
                    </el-form-item>
                </section>

                <section class="time-edit-section time-rate-grid">
                    <el-form-item :label="$t('time.ratePreset')">
                        <el-select v-model="selectedRateIndex" clearable style="width: 100%" @change="applyRate" :placeholder="$t('time.customRate')">
                            <el-option v-for="(rate, index) in configuredRates" :key="index" :label="`${rate.name} · ${money(rate.rate)}`" :value="index"></el-option>
                        </el-select>
                    </el-form-item>
                    <el-form-item :label="$t('common.rate')">
                        <el-input v-model.number="form.rate" type="number" min="0" step="0.01"></el-input>
                    </el-form-item>
                    <el-form-item :label="$t('time.rateName')">
                        <el-input v-model="form.rateName" maxlength="255"></el-input>
                    </el-form-item>
                    <el-form-item :label="$t('time.showRateName')">
                        <el-switch v-model="form.showRateName" :active-value="1" :inactive-value="0"></el-switch>
                    </el-form-item>
                </section>

                <section class="time-edit-section">
                    <el-form-item :label="$t('common.description')">
                        <el-input v-model="form.timeDescription" type="textarea" :rows="5" maxlength="2000" show-word-limit></el-input>
                    </el-form-item>
                </section>

                <section class="time-total-card">
                    <span>{{$t('common.total')}}</span>
                    <strong>{{money(total)}}</strong>
                </section>
                <el-alert v-if="form.billed === 1" :title="$t('time.billedReadOnly')" type="info" :closable="false" show-icon></el-alert>
            </el-form>
        </section>
    </main>
</template>

<script>
    import moment from 'moment-timezone'
    import {timest, uuid} from '@/utils/validate'
    import {getCurrentCompany} from '@/utils/company'
    import {parseTimeRates} from '@/utils/timeTracker'
    import {xiaoshudianfenge} from '@/utils/moneyFormat'

    export default {
        data() {
            const now = moment().seconds(0).milliseconds(0)
            return {
                isNew: this.$route.path === '/timeCreate',
                loading: true,
                saving: false,
                clients: [],
                configuredRates: [],
                selectedRateIndex: null,
                durationHours: 0,
                durationMinutes: 0,
                company: getCurrentCompany(),
                userSetting: JSON.parse(localStorage.getItem('userSetting')),
                form: {
                    innerUUID: uuid(null, 37),
                    billClientUUID: '',
                    clientName: '',
                    start: now.toDate(),
                    end: now.toDate(),
                    rate: 0,
                    rateName: '',
                    showRateName: 0,
                    timeDescription: '',
                    billed: 0,
                    itemUUID: '',
                    createDate: timest()
                }
            }
        },
        computed: {
            totalMinutes() {
                return this.durationHours * 60 + this.durationMinutes
            },
            total() {
                return this.$NP.times(this.form.rate, this.totalMinutes / 60)
            }
        },
        methods: {
            load() {
                if (this.company == null) {
                    throw new Error('Current company is required')
                }
                this.configuredRates = parseTimeRates(this.company.timeRateInfo)
                if (this.isNew && this.configuredRates.length === 1) {
                    this.selectedRateIndex = 0
                    this.applyRate(0)
                }
                const requests = [this.$axios.post('query_user_client_user_web', {
                    params: {currentPage: 1, pageSize: 400, userWebCommonQuery: '', orderName: 'name', orderTpe: 1, delStatus: 0}
                })]
                if (!this.isNew) {
                    requests.push(this.$axios.post('query_time_tracker_user_web', {params: {
                        innerUUID: this.$route.query.innerUUID,
                        companyUUID: this.company.innerUUID
                    }}))
                }
                Promise.all(requests).then(responses => {
                    if (responses[0].data.errorCode !== 0) {
                        throw new Error(responses[0].data.errorMsg)
                    }
                    if (!Array.isArray(responses[0].data.dataList)) {
                        throw new Error('Client list response is invalid')
                    }
                    this.clients = responses[0].data.dataList
                    if (!this.isNew) {
                        if (responses[1].data.errorCode !== 0) {
                            throw new Error(responses[1].data.errorMsg)
                        }
                        if (responses[1].data.objectData == null) {
                            throw new Error('Time response is invalid')
                        }
                        this.applyExistingTime(responses[1].data.objectData)
                    }
                }).catch(error => {
                    this.$message.error(error.message)
                }).finally(() => {
                    this.loading = false
                })
            },
            applyExistingTime(time) {
                if (time.innerUUID !== this.$route.query.innerUUID || time.companyUUID !== this.company.innerUUID) {
                    throw new Error('Time does not belong to the selected company')
                }
                this.form = {
                    innerUUID: time.innerUUID,
                    billClientUUID: time.billClientUUID,
                    clientName: time.clientName,
                    start: moment.unix(Number(time.startDate)).toDate(),
                    end: moment.unix(Number(time.endDate)).toDate(),
                    rate: Number(time.rate),
                    rateName: time.rateName,
                    showRateName: time.showRateName,
                    timeDescription: time.timeDescription,
                    billed: time.billed,
                    itemUUID: time.itemUUID,
                    createDate: time.createDate
                }
                this.durationHours = Math.floor(time.totalMinutes / 60)
                this.durationMinutes = time.totalMinutes % 60
            },
            selectClient(innerUUID) {
                if (innerUUID === '') {
                    this.form.clientName = ''
                    return
                }
                const client = this.clients.find(item => item.innerUUID === innerUUID)
                if (client == null) {
                    throw new Error('Selected client is invalid')
                }
                this.form.clientName = client.name
            },
            applyRate(index) {
                if (index === '') {
                    this.selectedRateIndex = null
                    return
                }
                const selected = this.configuredRates[index]
                if (selected == null) {
                    throw new Error('Selected time rate is invalid')
                }
                this.form.rate = selected.rate
                this.form.rateName = selected.name
                this.form.showRateName = selected.showRateName
            },
            recalculateDuration() {
                const minutes = moment(this.form.end).diff(moment(this.form.start), 'minutes')
                if (minutes < 0) {
                    this.$message.error(this.$t('time.endBeforeStart'))
                    return
                }
                this.durationHours = Math.floor(minutes / 60)
                this.durationMinutes = minutes % 60
            },
            recalculateEnd() {
                this.form.end = moment(this.form.start).add(this.totalMinutes, 'minutes').toDate()
            },
            requestParams(syncStatus) {
                if (moment(this.form.end).isBefore(moment(this.form.start))) {
                    throw new Error(this.$t('time.endBeforeStart'))
                }
                if (typeof this.form.rate !== 'number' || !Number.isFinite(this.form.rate) || this.form.rate < 0) {
                    throw new Error(this.$t('time.invalidRate'))
                }
                return {
                    innerUUID: this.form.innerUUID,
                    fromClient: 1,
                    accessDate: timest(),
                    billClientUUID: this.form.billClientUUID,
                    clientName: this.form.clientName,
                    createDate: this.form.createDate,
                    endDate: String(moment(this.form.end).unix()),
                    rate: this.form.rate,
                    rateName: this.form.rateName,
                    showRateName: this.form.showRateName,
                    startDate: String(moment(this.form.start).unix()),
                    syncStatus,
                    timeDescription: this.form.timeDescription,
                    total: this.total,
                    totalMinutes: this.totalMinutes,
                    itemUUID: this.form.itemUUID,
                    billed: this.form.billed
                }
            },
            save() {
                if (this.form.billed === 1 && !this.isNew) {
                    this.$message.error(this.$t('time.billedReadOnly'))
                    return
                }
                let params
                try {
                    params = this.requestParams(0)
                } catch (error) {
                    this.$message.error(error.message)
                    return
                }
                this.saving = true
                const endpoint = this.isNew ? 'create_time_tracker' : 'update_time_tracker'
                this.$axios.post(endpoint, {params}).then(response => {
                    if (response.data.errorCode === 0) {
                        this.$message.success(this.$t('common.saveSuccess'))
                        this.$router.push('/time')
                    }
                }).finally(() => {
                    this.saving = false
                })
            },
            confirmDelete() {
                this.$confirm(this.$t('time.confirmDelete'), this.$t('common.warning'), {
                    confirmButtonText: this.$t('common.delete'), cancelButtonText: this.$t('common.cancel'), type: 'warning'
                }).then(() => this.deleteTime())
            },
            deleteTime() {
                this.loading = true
                this.$axios.post('update_time_tracker', {params: this.requestParams(1)}).then(response => {
                    if (response.data.errorCode === 0) {
                        this.$message.success(this.$t('common.deleteSuccess'))
                        this.$router.push('/time')
                    }
                }).finally(() => {
                    this.loading = false
                })
            },
            goBack() {
                this.$router.push('/time')
            },
            money(value) {
                return xiaoshudianfenge(value, this.userSetting)
            }
        },
        mounted() {
            this.load()
        }
    }
</script>

<style scoped>
    .time-edit-page { min-height: 100%; padding: 34px; box-sizing: border-box; background: #f4f7fb; }
    .time-edit-card { max-width: 980px; margin: 0 auto; background: #fff; border: 1px solid #e4eaf3; border-radius: 16px; box-shadow: 0 12px 34px rgba(41, 62, 96, .08); overflow: hidden; }
    .time-edit-header { display: flex; justify-content: space-between; align-items: center; padding: 24px 28px; border-bottom: 1px solid #e9eef5; }
    .time-edit-title { display: flex; align-items: center; gap: 14px; }
    .time-edit-title h1 { margin: 0; color: #17213c; font-size: 24px; }
    .time-back { border: 0; font-size: 18px; }
    .time-edit-form { padding: 28px; }
    .time-edit-section { padding: 22px; margin-bottom: 20px; border: 1px solid #e6ecf4; border-radius: 12px; background: #fff; }
    .time-edit-section h2 { margin: 0 0 16px; font-size: 15px; color: #52627c; }
    .time-date-grid, .time-rate-grid { display: grid; gap: 18px; }
    .time-date-grid { grid-template-columns: 1fr 1fr; }
    .time-date-grid .el-form-item:last-child { grid-column: 1 / -1; }
    .time-rate-grid { grid-template-columns: 1fr 1fr; }
    .duration-inputs { display: flex; align-items: center; gap: 10px; }
    .time-total-card { display: flex; justify-content: space-between; padding: 22px 26px; margin-bottom: 20px; color: #fff; background: #17213c; border-radius: 12px; font-size: 20px; }
    .time-total-card strong { font-size: 25px; }
    @media (max-width: 720px) { .time-edit-page { padding: 16px; } .time-date-grid, .time-rate-grid { grid-template-columns: 1fr; } }
</style>
